// covered-by: frontends/carrie/src/routes/settings/routes/knowledge-database/components/__tests__/ActivityPanel.test.tsx
import { CaretDown, CaretRight, Robot } from "@phosphor-icons/react";
import { Button, Flex, IconButton, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { AvatarWithInitials } from "../../../../../components/AvatarWithInitials";
import { isNil } from "../../../../../utils/fp";
import {
  KnowledgeActivityAction,
  KnowledgeActivityActorKind,
  KnowledgeActivityTargetType,
  type KnowledgeActivityResult,
} from "../../../../../utils/parsers/knowledge";
import {
  useKnowledgeActivityQuery,
  useLoadMoreKnowledgeActivityMutation,
} from "../../../../../utils/queries/knowledge";
import { useGetUsers } from "../../../../../utils/queries/users";
import { groupActivities, groupSentence } from "../activityGrouping";
import { knowledgeDatabaseRoute } from "../route";

const PAGE_SIZE = 50;

const ACTION_SENTENCES: Record<KnowledgeActivityAction, string> = {
  [KnowledgeActivityAction.SOURCE_UPLOADED]: "uploaded",
  [KnowledgeActivityAction.SOURCE_READY]: "finished processing",
  [KnowledgeActivityAction.SOURCE_FAILED]: "failed to process",
  [KnowledgeActivityAction.SOURCE_SCOPE_CHANGED]: "changed the scope of",
  [KnowledgeActivityAction.SOURCE_NOTE_CHANGED]: "edited the note on",
  [KnowledgeActivityAction.SOURCE_DELETED]: "deleted",
  [KnowledgeActivityAction.SOURCE_EXTRACTION_STARTED]:
    "started extracting rules from",
  [KnowledgeActivityAction.SOURCE_EXTRACTION_FAILED]:
    "failed to extract rules from",
  [KnowledgeActivityAction.RULE_CREATED]: "created the rule",
  [KnowledgeActivityAction.RULE_UPDATED]: "added evidence to the rule",
  [KnowledgeActivityAction.RULE_PROMOTED]: "promoted the suggestion",
  [KnowledgeActivityAction.RULE_REJECTED]: "rejected the suggestion",
  [KnowledgeActivityAction.RULE_ARCHIVED]: "archived the rule",
  [KnowledgeActivityAction.RULE_SCOPE_CHANGED]: "changed the scope of the rule",
  [KnowledgeActivityAction.RULE_SUPERSEDED]: "superseded the rule",
};

/**
 * Append-only audit trail, newest first: who (user avatar or the workflow
 * robot) did what to which source/rule, with target links that jump to the
 * Rules/Sources tab. Workflow rule bursts (one row per rule extracted from a
 * document) collapse into one expandable "extracted N rules" entry. Explicit
 * Load more on the keyset cursor.
 */
export function ActivityPanel() {
  const { t } = useTranslation("ui");
  const navigate = knowledgeDatabaseRoute.useNavigate();
  const activityQuery = useKnowledgeActivityQuery();
  const loadMore = useLoadMoreKnowledgeActivityMutation();
  const usersQuery = useGetUsers();

  const [extra, setExtra] = useState<KnowledgeActivityResult[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [more, setMore] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(
    () => new Set(),
  );

  useEffect(() => {
    setExtra([]);
    setCursor(activityQuery.data?.nextCursor ?? null);
    setMore(activityQuery.data?.more ?? false);
  }, [activityQuery.data]);

  const activities = useMemo(
    () => [...(activityQuery.data?.activities ?? []), ...extra],
    [activityQuery.data, extra],
  );
  const feed = useMemo(() => groupActivities(activities), [activities]);

  function toggleGroup(id: string) {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  const userName = (userId: string | null | undefined): string => {
    if (!userId) return "A teammate";
    const user = (usersQuery.data ?? []).find((u) => u._id === userId);
    return user?.name ?? "A teammate";
  };

  function goToTarget(activity: KnowledgeActivityResult) {
    if (activity.target.type === KnowledgeActivityTargetType.RULE) {
      navigate({
        search: (prev) => ({
          ...prev,
          tab: "rules",
          ruleId: activity.target.id,
        }),
      });
    } else {
      navigate({
        search: (prev) => ({ ...prev, tab: "sources", ruleId: undefined }),
      });
    }
  }

  function renderActivityRow(activity: KnowledgeActivityResult) {
    const isWorkflow =
      activity.actor.kind === KnowledgeActivityActorKind.WORKFLOW;
    const actorName = isWorkflow ? "Puntt" : userName(activity.actor.userId);
    return (
      <Flex
        key={activity._id}
        gap="2"
        align="center"
        data-testid={`knowledge-activity-item-${activity._id}`}
      >
        {isWorkflow ? (
          <Robot aria-hidden="true" className="text-gray-9 size-6 shrink-0" />
        ) : (
          <AvatarWithInitials size={6} name={actorName} />
        )}
        <Text size="2">
          <Text weight="bold">{actorName}</Text>{" "}
          {ACTION_SENTENCES[activity.action] ?? activity.action}{" "}
          <button
            type="button"
            onClick={() => goToTarget(activity)}
            aria-label={`Open ${activity.target.label ?? "target"}`}
            className="text-blue-11 cursor-pointer border-none bg-transparent p-0 font-medium underline"
            data-testid={`knowledge-activity-target-${activity._id}`}
          >
            {activity.target.label ?? activity.target.id}
          </button>
        </Text>
        <Text size="1" color="gray" className="ml-auto shrink-0">
          {new Date(activity.createdAt).toLocaleString()}
        </Text>
      </Flex>
    );
  }

  if (!activityQuery.isLoading && activities.length === 0) {
    return (
      <Flex
        direction="column"
        align="center"
        className="py-12"
        data-testid="knowledge-activity-empty"
      >
        <Text size="2" color="gray">
          No activity yet. Uploads, extractions, and rule changes will show up
          here.
        </Text>
      </Flex>
    );
  }

  return (
    <section
      aria-label={t("settings.activityPanel.knowledgeActivity")}
      data-testid="knowledge-activity-list"
    >
      <Flex direction="column" gap="3">
        {feed.map((item) => {
          if (item.kind === "single") {
            return renderActivityRow(item.activity);
          }
          const expanded = expandedGroups.has(item.id);
          return (
            <Flex
              key={`group-${item.id}`}
              direction="column"
              gap="2"
              data-testid={`knowledge-activity-group-${item.id}`}
            >
              <Flex gap="2" align="center">
                <Robot
                  aria-hidden="true"
                  className="text-gray-9 size-6 shrink-0"
                />
                <Text size="2">
                  <Text weight="bold">{t("settings.activityPanel.puntt")}</Text>{" "}
                  {groupSentence(item.action, item.activities.length)}
                </Text>
                <IconButton
                  size="1"
                  variant="ghost"
                  color="gray"
                  aria-label={
                    expanded
                      ? `Collapse ${item.activities.length} grouped activities`
                      : `Expand ${item.activities.length} grouped activities`
                  }
                  aria-expanded={expanded}
                  onClick={() => toggleGroup(item.id)}
                  data-testid={`knowledge-activity-group-toggle-${item.id}`}
                >
                  {expanded ? (
                    <CaretDown aria-hidden="true" />
                  ) : (
                    <CaretRight aria-hidden="true" />
                  )}
                </IconButton>
                <Text size="1" color="gray" className="ml-auto shrink-0">
                  {new Date(item.createdAt).toLocaleString()}
                </Text>
              </Flex>
              {expanded ? (
                <Flex
                  direction="column"
                  gap="2"
                  className="border-gray-5 ml-3 border-0 border-l border-solid pl-4"
                  data-testid={`knowledge-activity-group-items-${item.id}`}
                >
                  {item.activities.map(renderActivityRow)}
                </Flex>
              ) : null}
            </Flex>
          );
        })}
      </Flex>

      {more ? (
        <Flex justify="center" className="mt-4">
          <Button
            size="1"
            variant="soft"
            color="gray"
            loading={loadMore.isPending}
            onClick={() => {
              if (isNil(cursor)) return;
              loadMore.mutate(
                { pageSize: PAGE_SIZE, searchAfter: cursor },
                {
                  onSuccess(data) {
                    // parseResponse yields undefined on a malformed payload
                    // (already console-logged) — keep the current page.
                    if (isNil(data)) return;
                    setExtra((prev) => [...prev, ...data.activities]);
                    setCursor(data.nextCursor);
                    setMore(data.more);
                  },
                },
              );
            }}
            data-testid="knowledge-activity-load-more"
          >
            {t("settings.activityPanel.loadMore")}
          </Button>
        </Flex>
      ) : null}
    </section>
  );
}
